import moment from 'moment'

export function getGradeForJanDecIntake(childDOB, commencementDate) {
  // Table mapping for Jan-Dec intake (2025 and 2026)
  const gradeTable = [
    { grade: 'Pre-Nursery', yearOffset: 2 },
    { grade: 'Nursery 1', yearOffset: 3 },
    { grade: 'Nursery 2', yearOffset: 4 },
    { grade: 'Kindergarten 1', yearOffset: 5 },
    { grade: 'Kindergarten 2', yearOffset: 6 },
    { grade: 'Grade 1', yearOffset: 7 },
    { grade: 'Grade 2', yearOffset: 8 },
    { grade: 'Grade 3', yearOffset: 9 },
    { grade: 'Grade 4', yearOffset: 10 },
    { grade: 'Grade 5', yearOffset: 11 },
    { grade: 'Grade 6', yearOffset: 12 },
    { grade: 'Grade 7', yearOffset: 13 },
    { grade: 'Grade 8', yearOffset: 14 },
    { grade: 'Grade 9', yearOffset: 15 },
    { grade: 'Grade 10', yearOffset: 16 },
    { grade: 'Grade 11', yearOffset: 17 },
    { grade: 'Grade 12', yearOffset: 18 },
  ]

  if (!childDOB || !commencementDate) return null
  const dob = moment(childDOB, [moment.ISO_8601, 'YYYY-MM-DD', 'DD/MM/YYYY'])
  const commencement = moment(commencementDate, [moment.ISO_8601, 'YYYY-MM-DD', 'DD/MM/YYYY'])
  if (!dob.isValid() || !commencement.isValid()) return null

  const commencementYear = commencement.year()
  const birthYear = dob.year()
  const ageAtCommencement = commencementYear - birthYear

  // Special case for Pre-Nursery: 18m-2y (must be born in commencementYear-2)
  // For all other grades, match yearOffset
  for (let i = 0; i < gradeTable.length; i++) {
    if (ageAtCommencement === gradeTable[i].yearOffset) {
      // For Pre-Nursery, check if child is at least 18 months at commencement
      if (gradeTable[i].grade === 'Pre-Nursery') {
        const months = commencement.diff(dob, 'months')
        if (months >= 18 && months < 36) {
          return gradeTable[i].grade
        }
      } else {
        return gradeTable[i].grade
      }
    }
  }
  return null
}

export function getGradeForAugJunIntake(childDOB, commencementDate) {
  if (!childDOB || !commencementDate) return null
  const dob = moment(childDOB, [moment.ISO_8601, 'YYYY-MM-DD', 'DD/MM/YYYY'])
  const commencement = moment(commencementDate, [moment.ISO_8601, 'YYYY-MM-DD', 'DD/MM/YYYY'])
  if (!dob.isValid() || !commencement.isValid()) return null

  // Determine the academic year based on commencement date
  const commencementYear = commencement.year()
  let academicYear
  if (commencement.month() >= 7) {
    // August (7) or later
    academicYear = commencementYear
  } else {
    academicYear = commencementYear - 1
  }

  // Base year for birth date ranges (2024-2025 academic year from the table)
  const baseAcademicYear = 2024
  const yearOffset = academicYear - baseAcademicYear

  // Grade definitions with birth year ranges for 2024-2025 academic year
  // Each subsequent year, all birth years shift by +1
  const gradeRanges = [
    { grade: 'Pre-Nursery', birthYearStart: 2022, birthYearEnd: 2023, startMonth: 9, startDay: 1, endMonth: 2, endDay: 28, special: true }, // Sept-Feb, 18m-3y
    { grade: 'Nursery 1', birthYearStart: 2021, birthYearEnd: 2022, startMonth: 9, startDay: 1, endMonth: 8, endDay: 31 }, // Sept 21-Aug 22
    { grade: 'Nursery 2', birthYearStart: 2020, birthYearEnd: 2021, startMonth: 9, startDay: 1, endMonth: 8, endDay: 31 }, // Sept 20-Aug 21
    { grade: 'Kindergarten 1', birthYearStart: 2019, birthYearEnd: 2020, startMonth: 9, startDay: 1, endMonth: 8, endDay: 31 }, // Sept 19-Aug 20
    { grade: 'Year 1 (K2)', birthYearStart: 2018, birthYearEnd: 2019, startMonth: 9, startDay: 1, endMonth: 8, endDay: 31 }, // Sept 18-Aug 19
    { grade: 'Year 2', birthYearStart: 2017, birthYearEnd: 2018, startMonth: 9, startDay: 1, endMonth: 8, endDay: 31 }, // Sept 17-Aug 18
    { grade: 'Year 3', birthYearStart: 2016, birthYearEnd: 2017, startMonth: 9, startDay: 1, endMonth: 8, endDay: 31 }, // Sept 16-Aug 17
    { grade: 'Year 4', birthYearStart: 2015, birthYearEnd: 2016, startMonth: 9, startDay: 1, endMonth: 8, endDay: 31 }, // Sept 15-Aug 16
    { grade: 'Year 5', birthYearStart: 2014, birthYearEnd: 2015, startMonth: 9, startDay: 1, endMonth: 8, endDay: 31 }, // Sept 14-Aug 15
    { grade: 'Year 6', birthYearStart: 2013, birthYearEnd: 2014, startMonth: 9, startDay: 1, endMonth: 8, endDay: 31 }, // Sept 13-Aug 14
    { grade: 'Year 7', birthYearStart: 2012, birthYearEnd: 2013, startMonth: 9, startDay: 1, endMonth: 8, endDay: 31 }, // Sept 12-Aug 13
    { grade: 'Year 8', birthYearStart: 2011, birthYearEnd: 2012, startMonth: 9, startDay: 1, endMonth: 8, endDay: 31 }, // Sept 11-Aug 12
    { grade: 'Year 9', birthYearStart: 2010, birthYearEnd: 2011, startMonth: 9, startDay: 1, endMonth: 8, endDay: 31 }, // Sept 10-Aug 11
    { grade: 'Year 10', birthYearStart: 2009, birthYearEnd: 2010, startMonth: 9, startDay: 1, endMonth: 8, endDay: 31 }, // Sept 09-Aug 10
    { grade: 'Year 11', birthYearStart: 2008, birthYearEnd: 2009, startMonth: 9, startDay: 1, endMonth: 8, endDay: 31 }, // Sept 08-Aug 09
    { grade: 'Year 12', birthYearStart: 2007, birthYearEnd: 2008, startMonth: 9, startDay: 1, endMonth: 8, endDay: 31 }, // Sept 07-Aug 08
    { grade: 'Year 13', birthYearStart: 2006, birthYearEnd: 2007, startMonth: 9, startDay: 1, endMonth: 8, endDay: 31 }, // Sept 06-Aug 07
  ]

  // Check each grade range
  for (const range of gradeRanges) {
    // Adjust birth years for the current academic year
    const adjustedStartYear = range.birthYearStart + yearOffset
    const adjustedEndYear = range.birthYearEnd + yearOffset

    let startDate, endDate

    if (range.special) {
      // Pre-Nursery spans from Sept of start year to Feb of end year
      startDate = moment({ year: adjustedStartYear, month: range.startMonth - 1, day: range.startDay })
      endDate = moment({ year: adjustedEndYear, month: range.endMonth - 1 }).endOf('month')

      // Check if DOB falls in range
      if (dob.isSameOrAfter(startDate) && dob.isSameOrBefore(endDate)) {
        // Additional age check for Pre-Nursery (18m-3y)
        const months = commencement.diff(dob, 'months')
        if (months >= 18 && months < 36) {
          return range.grade
        }
      }
    } else {
      // All other grades span from Sept of start year to Aug of end year
      startDate = moment({ year: adjustedStartYear, month: range.startMonth - 1, day: range.startDay })
      endDate = moment({ year: adjustedEndYear, month: range.endMonth - 1, day: range.endDay })

      // Check if DOB falls in range
      if (dob.isSameOrAfter(startDate) && dob.isSameOrBefore(endDate)) {
        return range.grade
      }
    }
  }

  return null
}
